import _ButtonBase from "@mui/material/ButtonBase";
import _CircularProgress from "@mui/material/CircularProgress";
import _Card from "@mui/material/Card";
import _CardContent from "@mui/material/CardContent";
import _InputAdornment from "@mui/material/InputAdornment";
import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import _Close from "@mui/icons-material/Close";
import _Box from "@mui/material/Box";
import _TextField from "@mui/material/TextField";
import _Typography from "@mui/material/Typography";
import _InfoOutlined from "@mui/icons-material/InfoOutlined";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import { useFormik } from 'formik';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { Fragment, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Button as CustomButton, FileInput, InfoCard, RocketIcon, StickyBar } from '../components';
import { useCancellablePromise } from '../hooks';
import { challengeTypes, collaborationTypes } from '../models';
import { backendChallengeTypes, GeogroupService } from '../services';
import CancelChallengeEditionDialog from './cancel-challenge-edition-dialog';
export function ChallengesCustomForm({
  Button: _Button,
  Link,
  prevPath,
  challenge,
  currentPartner,
  disabledTypes,
  disabledTypesTooltip,
  drawerWidth,
  onCreated,
  onRedirect
}) {
  const [cancelDialogOpen, openCancelDialog] = useState(false);
  const [file, setFile] = useState(challenge?.photo ? {
    url: `${challenge.photo}`,
    name: challenge.photo.substring(challenge.photo.lastIndexOf('/') + 1)
  } : undefined);
  const {
    isValid,
    isSubmitting,
    values,
    errors,
    touched,
    setFieldValue,
    handleBlur,
    handleChange,
    handleSubmit,
    validateForm
  } = useFormik({
    initialValues: {
      title: challenge?.title || '',
      description: challenge?.description || '',
      startDate: challenge?.startDate.format('YYYY-MM-DD') || '',
      endDate: challenge?.endDate.format('YYYY-MM-DD') || '',
      type: challenge?.type || 'traveledDistance',
      collaborationType: challenge ? challenge.subGroupDisplay === 'companySite' ? 'sites' : challenge.subGroupDisplay ? 'teams' : challenge.collaborationType : 'individual',
      target: challenge?.targetValue ? challenge?.type === 'traveledDistance' ? challenge?.targetValue / 1000 : challenge?.targetValue : ''
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required().test('len', '', val => val ? val.toString().length <= 40 : false),
      description: Yup.string().test('len', '', val => val ? val.toString().length <= 500 : true),
      startDate: Yup.string().required(),
      endDate: Yup.string().required(),
      target: Yup.number().when('global', {
        is: 'teams',
        then: schema => schema.required()
      })
    }),
    onSubmit,
    enableReinitialize: true
  });
  const {
    t
  } = useTranslation();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    cancellablePromise,
    cancelPromises
  } = useCancellablePromise();
  useEffect(() => {
    validateForm();
    return () => cancelPromises();
  }, []);
  async function onSubmit({
    title,
    description,
    startDate,
    endDate,
    type,
    target,
    collaborationType
  }) {
    if (!currentPartner) return;
    try {
      let newChallenge;
      if (challenge) {
        const isStarted = moment(startDate).isBefore(moment());
        const data = {
          id: challenge.id,
          title,
          description,
          ...(!isStarted && !challenge.eventId ? {
            collaboration_type: collaborationType === 'global' ? 'EVERYONE' : 'INDIVIDUAL',
            start_datetime: moment(startDate).startOf('day').format(),
            end_datetime: moment(endDate).endOf('day').format(),
            target_type: backendChallengeTypes[type],
            trace_type: 'ALL',
            target_value: !target ? undefined : type === 'traveledDistance' ? target * 1000 : target,
            default_sub_group_display: collaborationType === 'teams' ? 'COMPANY_DEPARTMENT' : collaborationType === 'sites' ? 'COMPANY_SITE' : null
          } : {})
        };
        newChallenge = await cancellablePromise(GeogroupService.updateChallenge(currentPartner, data, file instanceof File ? file : undefined));
      } else {
        newChallenge = await cancellablePromise(GeogroupService.addChallenge(currentPartner, {
          title,
          description,
          collaboration_type: collaborationType === 'global' ? 'EVERYONE' : 'INDIVIDUAL',
          default_sub_group_display: collaborationType === 'teams' ? 'COMPANY_DEPARTMENT' : collaborationType === 'sites' ? 'COMPANY_SITE' : null,
          start_datetime: moment(startDate).startOf('day').format(),
          end_datetime: moment(endDate).endOf('day').format(),
          target_type: backendChallengeTypes[type],
          trace_type: 'ALL',
          target_value: typeof target === 'number' ? type === 'traveledDistance' ? target * 1000 : target : undefined
        }, file instanceof File ? file : undefined));
      }
      enqueueSnackbar(t('commons.challenge_post.form.success', {
        context: challenge ? 'update' : ''
      }));
      onCreated(newChallenge);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('commons.challenge_post.form.server_error', {
          context: challenge ? 'update' : ''
        }), {
          variant: 'error'
        });
      }
    }
  }
  function handleCancelDialogClose(leave) {
    openCancelDialog(false);
    if (leave) onRedirect();
  }
  if (challenge?.period === 'past') return /*#__PURE__*/React.createElement(React.Fragment, null);
  const Button = _Button || CustomButton;
  return /*#__PURE__*/React.createElement(_Box, {
    alignSelf: "stretch",
    component: "form",
    onSubmit: handleSubmit
  }, /*#__PURE__*/React.createElement(_Card, {
    elevation: 0,
    sx: {
      alignSelf: 'stretch',
      borderRadius: 4,
      marginBottom: '58px'
    }
  }, /*#__PURE__*/React.createElement(_CardContent, {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      padding: 3
    }
  }, challenge?.period === 'current' && /*#__PURE__*/React.createElement(InfoCard, {
    iconBackground: true,
    description: t('commons.challenge_post.form.current_info'),
    descriptionProps: {
      color: 'inherit',
      variant: 'body2'
    },
    elevation: 0,
    Icon: _InfoOutlined,
    iconProps: {
      color: 'secondary'
    },
    sx: {
      backgroundColor: '#eef8f4',
      borderRadius: '16px',
      padding: '8px'
    }
  }), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontSize: "1.125rem",
    fontWeight: 700
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.challenge_post.form.subtitle"
  })), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 2
  }, /*#__PURE__*/React.createElement(_TextField, {
    required: true,
    disabled: isSubmitting || challenge?.period === 'current' || Boolean(challenge?.eventId),
    error: touched.startDate && Boolean(errors.startDate),
    id: "startDate",
    label: t('commons.challenge_post.form.start_date'),
    margin: "dense",
    name: "startDate",
    onBlur: handleBlur,
    onChange: handleChange,
    size: "small",
    slotProps: {
      htmlInput: {
        min: moment().add(1, 'day').format('YYYY-MM-DD')
      },
      inputLabel: {
        shrink: true
      }
    },
    type: "date",
    value: values.startDate,
    variant: "outlined"
  }), /*#__PURE__*/React.createElement(_TextField, {
    required: true,
    disabled: isSubmitting || challenge?.period === 'current' || Boolean(challenge?.eventId),
    error: touched.endDate && Boolean(errors.endDate),
    id: "endDate",
    label: t('commons.challenge_post.form.end_date'),
    margin: "dense",
    name: "endDate",
    onBlur: handleBlur,
    onChange: handleChange,
    size: "small",
    slotProps: {
      htmlInput: {
        min: values.startDate || moment().add(1, 'day').format('YYYY-MM-DD')
      },
      inputLabel: {
        shrink: true
      }
    },
    type: "date",
    value: values.endDate,
    variant: "outlined"
  })), /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    required: true,
    disabled: isSubmitting,
    error: touched.title && Boolean(errors.title),
    id: "title",
    label: t('commons.challenge_post.form.name'),
    margin: "none",
    name: "title",
    onBlur: handleBlur,
    onChange: handleChange,
    placeholder: t('commons.challenge_post.form.name_placeholder'),
    size: "small",
    slotProps: {
      htmlInput: {
        maxLength: 38
      },
      inputLabel: {
        shrink: true
      }
    },
    value: values.title,
    variant: "outlined"
  }), /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    multiline: true,
    disabled: isSubmitting,
    error: touched.description && Boolean(errors.description),
    id: "description",
    label: t('commons.challenge_post.form.description'),
    margin: "none",
    name: "description",
    onBlur: handleBlur,
    onChange: handleChange,
    placeholder: t('commons.challenge_post.form.description_placeholder'),
    rows: 2,
    slotProps: {
      htmlInput: {
        maxLength: 500
      },
      inputLabel: {
        shrink: true
      }
    },
    value: values.description,
    variant: "outlined"
  }), !challenge?.eventId && /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2
  }, /*#__PURE__*/React.createElement(FileInput, {
    disablePreview: true,
    disabled: isSubmitting,
    emptyStateLabel: t('commons.challenge_post.form.photo'),
    file: file,
    onChange: setFile,
    size: "small",
    type: "image"
  }), file && /*#__PURE__*/React.createElement(_Box, {
    alignSelf: "flex-start",
    padding: "12px 12px 0 0",
    position: "relative"
  }, /*#__PURE__*/React.createElement("img", {
    height: 80,
    src: 'url' in file ? file.url : URL.createObjectURL(file),
    style: {
      borderRadius: 4
    }
  }), /*#__PURE__*/React.createElement(_Box, {
    position: "absolute",
    right: 0,
    top: 0
  }, /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "rgba(0, 0, 0, 0.8)",
    borderRadius: "50%",
    color: "#fff"
  }, /*#__PURE__*/React.createElement(_Tooltip, {
    title: t('commons.actions.remove')
  }, /*#__PURE__*/React.createElement(_IconButton, {
    color: "inherit",
    onClick: () => setFile(null),
    size: "small",
    sx: {
      padding: 0.5
    }
  }, /*#__PURE__*/React.createElement(_Close, {
    fontSize: "small"
  })))))))), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 3
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontSize: "1.125rem",
    fontWeight: 700
  }, t('commons.challenge_post.form.competition_type')), /*#__PURE__*/React.createElement(_Typography, null, t('commons.challenge_post.form.competition_type_description'))), /*#__PURE__*/React.createElement(_Box, {
    columnGap: 2,
    display: "flex",
    flexDirection: {
      xs: 'column',
      lg: 'row'
    },
    rowGap: 1
  }, collaborationTypes.filter(({
    key
  }) => key !== 'teams' && key !== 'sites').map(({
    key,
    ...props
  }) => {
    const active = values.collaborationType === key;
    return /*#__PURE__*/React.createElement(CustomChallengeFormTypeCard, _extends({
      active: active,
      disabled: challenge && (challenge.period !== 'future' || Boolean(challenge.eventId)) || false,
      key: key,
      onSelect: () => setFieldValue('collaborationType', key)
    }, props));
  }))), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontSize: "1.125rem",
    fontWeight: 700
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.challenge_post.form.type"
  })), /*#__PURE__*/React.createElement(_Typography, null, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.challenge_post.form.type_description"
  }))), /*#__PURE__*/React.createElement(_Box, {
    columnGap: 2,
    display: "flex",
    flexDirection: {
      xs: 'column',
      lg: 'row'
    },
    rowGap: 1
  }, challengeTypes.map(({
    key,
    ...props
  }) => {
    if (key === 'journeysCount') return /*#__PURE__*/React.createElement(Fragment, {
      key: key
    });
    const active = values.type === key;
    const isTypeDisabled = disabledTypes?.includes(key) ?? false;
    return /*#__PURE__*/React.createElement(CustomChallengeFormTypeCard, _extends({
      active: active,
      disabled: isTypeDisabled || challenge && (challenge.period !== 'future' || Boolean(challenge.eventId)) || false,
      key: key,
      onSelect: () => setFieldValue('type', key),
      tooltip: isTypeDisabled ? disabledTypesTooltip : undefined
    }, props));
  })), values.collaborationType === 'global' && /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    required: true,
    disabled: isSubmitting || challenge?.period === 'current' || Boolean(challenge?.eventId),
    error: touched.target && Boolean(errors.target),
    id: "target",
    label: t('commons.challenge_post.form.target', {
      context: values.type === 'traveledDistance' ? 'distance' : values.type === 'cyclingDays' ? 'days' : values.type === 'journeysCount' || values.type === 'homeWorkJourneysCount' ? 'journeys' : ''
    }),
    margin: "dense",
    name: "target",
    onBlur: handleBlur,
    onChange: handleChange,
    placeholder: t('commons.challenge_post.form.target_placeholder', {
      context: values.type === 'traveledDistance' ? 'distance' : 'days'
    }),
    size: "small",
    slotProps: {
      htmlInput: {
        min: 0
      },
      input: {
        endAdornment: values.type === 'traveledDistance' && /*#__PURE__*/React.createElement(_InputAdornment, {
          position: "end"
        }, "km")
      },
      inputLabel: {
        shrink: true
      }
    },
    type: "number",
    value: values.target,
    variant: "outlined"
  })))), /*#__PURE__*/React.createElement(StickyBar, {
    drawerWidth: drawerWidth
  }, /*#__PURE__*/React.createElement(React.Fragment, null, challenge ? /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    disabled: isSubmitting,
    onClick: () => openCancelDialog(true),
    size: "large",
    startIcon: /*#__PURE__*/React.createElement(_Close, null),
    variant: "outlined"
  }, t('commons.actions.cancel')) : /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    component: Link,
    disabled: isSubmitting,
    relative: "path",
    size: "large",
    startIcon: /*#__PURE__*/React.createElement(_Close, null),
    to: prevPath,
    variant: "outlined"
  }, t('commons.actions.cancel')), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    disabled: !isValid || isSubmitting,
    size: "large",
    startIcon: isSubmitting ? /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }) : /*#__PURE__*/React.createElement(RocketIcon, null),
    type: "submit",
    variant: "contained"
  }, t(challenge ? 'commons.actions.save' : 'commons.challenge_post.actions.launch')))), /*#__PURE__*/React.createElement(CancelChallengeEditionDialog, {
    handleClose: handleCancelDialogClose,
    open: cancelDialogOpen
  }));
}
export function CustomChallengeFormTypeCard({
  currentPartnerType,
  active,
  disabled,
  Icon,
  labelKey,
  descriptionKey,
  onSelect,
  tooltip
}) {
  const {
    t
  } = useTranslation();
  return /*#__PURE__*/React.createElement(_Tooltip, {
    title: tooltip && disabled ? tooltip : ''
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    width: {
      xs: '100%',
      lg: 'calc((100% - 48px) / 2)'
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    component: _ButtonBase,
    disabled: disabled,
    onClick: onSelect,
    sx: {
      alignItems: 'flex-start',
      bgcolor: active ? '#ECFBF4' : disabled ? '#F6F8FC' : 'transparent',
      border: !active && disabled ? 0 : `1px solid ${active ? '#2AC682' : '#DDE0E7'}`,
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      gap: 1,
      justifyContent: 'flex-start',
      opacity: !active && disabled ? 0.6 : 1,
      padding: 2,
      textAlign: 'left'
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    gap: 1
  }, Icon && /*#__PURE__*/React.createElement(Icon, {
    color: !active && disabled ? 'action' : 'primary'
  }), /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    variant: "body2"
  }, t(labelKey, {
    context: currentPartnerType || ''
  }))), descriptionKey && /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    variant: "caption"
  }, t(descriptionKey, {
    context: currentPartnerType || ''
  })))));
}